/**
 * AudioOverviewPanel - Static Audio Overview generation
 *
 * Uses Google Vertex AI for audio overview generation.
 * STATIC ONLY - No interactivity, just batch MP3 generation.
 *
 * @module AudioOverviewPanel
 */

import React, { useState, useEffect, useRef } from 'react';

import {
  Headphones,
  Loader2,
  Play,
  Pause,
  Download,
  RefreshCw,
  Volume2,
  VolumeX,
  SkipBack,
  SkipForward,
  Clock,
  AlertTriangle,
  Sparkles,
  Mic2,
  Users,
} from 'lucide-react';

import ideasKitchenApi from '@/pages/ideas-kitchen/api/ideasKitchenApi';
import { FEATURE_FLAGS } from '../../../config/featureFlags';

const AudioOverviewPanel = ({ notebook, quotaStatus }) => {
  const audioEnabled = FEATURE_FLAGS.IDEAS_KITCHEN_AUDIO_ENABLED === true;
  const [audioStyle, setAudioStyle] = useState('conversational');
  const [audioDuration, setAudioDuration] = useState('auto');
  const [topicFocus, setTopicFocus] = useState('');

  const [audioJobs, setAudioJobs] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);

  // Player state
  const [currentAudio, setCurrentAudio] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  const audioRef = useRef(null);

  // Load existing audio overviews
  useEffect(() => {
    if (audioEnabled && notebook?.id) {
      loadAudioOverviews();
    }
  }, [audioEnabled, notebook?.id]);

  const loadAudioOverviews = async () => {
    try {
      const response = await ideasKitchenApi.listAudioOverviews(notebook.id);
      if (response.success && response.audioOverviews) {
        setAudioJobs(response.audioOverviews);
      }
    } catch (err) {
      console.error('Failed to load audio overviews:', err);
    }
  };

  const handleGenerateAudio = async () => {
    if (!notebook?.id) return;

    setIsGenerating(true);
    setError(null);

    try {
      const response = await ideasKitchenApi.generateAudioOverview(notebook.id, {
        style: audioStyle,
        duration: audioDuration,
        focus: topicFocus.trim() || null,
      });

      if (response.success) {
        // Add the new job to the list
        if (response.job) {
          setAudioJobs(prev => [response.job, ...prev]);
          // Start polling for completion
          pollAudioStatus(response.job.id);
        } else if (response.message) {
          // Handle case where audio generation is not available
          setError(response.message || 'Audio generation not available');
        }
      } else {
        throw new Error(response.error || 'Failed to start audio generation');
      }
    } catch (err) {
      console.error('Audio generation failed:', err);
      setError(err.message || 'Failed to generate audio');
    } finally {
      setIsGenerating(false);
    }
  };

  const pollAudioStatus = async jobId => {
    const poll = async () => {
      try {
        const response = await ideasKitchenApi.getAudioStatus(notebook.id, jobId);
        if (response.success && response.job) {
          setAudioJobs(prev => prev.map(j => (j.id === jobId ? response.job : j)));

          if (response.job.status === 'PROCESSING' || response.job.status === 'PENDING') {
            // Continue polling
            setTimeout(poll, 5000);
          }
        }
      } catch (err) {
        console.error('Failed to poll audio status:', err);
      }
    };
    poll();
  };

  const handlePlayAudio = audio => {
    if (currentAudio?.id === audio.id) {
      // Toggle play/pause
      if (isPlaying) {
        audioRef.current?.pause();
      } else {
        audioRef.current?.play();
      }
    } else {
      // Play new audio
      setCurrentAudio(audio);
      setCurrentTime(0);
      setTimeout(() => {
        audioRef.current?.play();
      }, 100);
    }
  };

  const formatTime = seconds => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const sources = notebook?.sources || [];
  const audioQuota = quotaStatus?.usage || {};
  const audioLimit = Number(audioQuota.dailyCostLimit || 0);
  const audioUsed = Number(audioQuota.dailyCostUsed || 0);
  const audioQuotaBlocked = audioLimit > 0 && audioUsed >= audioLimit;

  if (!audioEnabled) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 text-center bg-white dark:bg-neutral-900">
        <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-2xl mb-4">
          <Headphones className="h-10 w-10 text-purple-500 dark:text-purple-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
          Audio Overview is disabled
        </h3>
        <p className="text-sm text-slate-500 dark:text-neutral-400 max-w-md">
          Audio generation is currently unavailable for this environment or plan. Please contact an
          administrator to enable it.
        </p>
      </div>
    );
  }

  if (audioQuotaBlocked) {
    return (
      <div className="h-full flex flex-col items-center justify-center px-6 text-center bg-white dark:bg-neutral-900">
        <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-2xl mb-4">
          <AlertTriangle className="h-10 w-10 text-red-500 dark:text-red-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
          Audio quota reached
        </h3>
        <p className="text-sm text-slate-500 dark:text-neutral-400 max-w-md">
          Audio generation is temporarily unavailable for this notebook. Please wait for quota reset
          or contact an administrator to increase limits.
        </p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Configuration */}
      <div className="flex-shrink-0 p-4 border-b border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 space-y-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-purple-400 to-pink-500 rounded-xl">
            <Headphones className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-white">Audio Overview</h3>
            <p className="text-xs text-slate-500 dark:text-neutral-400">
              Generate a podcast-style audio summary
            </p>
          </div>
        </div>

        {/* Style Selection */}
        <div>
          <label className="block text-xs font-medium text-slate-500 dark:text-neutral-400 mb-2">
            Style
          </label>
          <div className="flex gap-2">
            {[
              { id: 'conversational', label: 'Conversational', icon: Users },
              { id: 'professional', label: 'Professional', icon: Mic2 },
            ].map(style => (
              <button
                key={style.id}
                onClick={() => setAudioStyle(style.id)}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  audioStyle === style.id
                    ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800'
                    : 'bg-slate-100 dark:bg-neutral-800 text-slate-600 dark:text-neutral-400 border border-transparent hover:border-slate-200 dark:hover:border-neutral-700'
                }`}
              >
                <style.icon className="h-4 w-4" />
                {style.label}
              </button>
            ))}
          </div>
        </div>

        {/* Optional Topic Focus */}
        <div>
          <label className="block text-xs font-medium text-slate-500 dark:text-neutral-400 mb-1.5">
            Topic Focus (optional)
          </label>
          <input
            type="text"
            value={topicFocus}
            onChange={e => setTopicFocus(e.target.value)}
            placeholder="Leave empty for full overview..."
            className="w-full px-3 py-2 bg-slate-100 dark:bg-neutral-800 border-0 rounded-lg text-sm focus:ring-2 focus:ring-purple-500"
          />
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerateAudio}
          disabled={isGenerating || !sources.length}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md"
        >
          {isGenerating ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Starting Generation...
            </>
          ) : (
            <>
              <Sparkles className="h-5 w-5" />
              Generate Audio
            </>
          )}
        </button>

        {!sources.length && (
          <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <p className="text-xs text-amber-700 dark:text-amber-300">
              Upload some sources first to generate audio
            </p>
          </div>
        )}

        {/* Note about static audio */}
        <div className="flex items-center gap-2 px-3 py-2 bg-slate-100 dark:bg-neutral-800 rounded-lg">
          <Clock className="h-4 w-4 text-slate-500 dark:text-neutral-400" />
          <p className="text-xs text-slate-500 dark:text-neutral-400">
            Audio generation may take a few minutes
          </p>
        </div>
      </div>

      {/* Audio Player */}
      {currentAudio && (
        <div className="flex-shrink-0 p-4 border-b border-slate-200 dark:border-neutral-700 bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
          <audio
            ref={audioRef}
            src={currentAudio.audioUrl}
            onTimeUpdate={e => setCurrentTime(e.target.currentTime)}
            onDurationChange={e => setDuration(e.target.duration)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          />

          <div className="flex items-center gap-4">
            {/* Play/Pause */}
            <button
              onClick={() => (isPlaying ? audioRef.current?.pause() : audioRef.current?.play())}
              className="p-3 bg-purple-500 text-white rounded-full hover:bg-purple-600 transition-colors shadow-md"
            >
              {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </button>

            {/* Progress */}
            <div className="flex-1">
              <input
                type="range"
                min={0}
                max={duration || 100}
                value={currentTime}
                onChange={e => {
                  const time = parseFloat(e.target.value);
                  setCurrentTime(time);
                  if (audioRef.current) {
                    audioRef.current.currentTime = time;
                  }
                }}
                className="w-full h-2 bg-purple-200 dark:bg-purple-800 rounded-full appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-purple-500"
              />
              <div className="flex justify-between text-xs text-slate-500 dark:text-neutral-400 mt-1">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Volume */}
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="p-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
            >
              {isMuted ? (
                <VolumeX className="h-5 w-5 text-slate-500" />
              ) : (
                <Volume2 className="h-5 w-5 text-slate-500" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex-shrink-0 px-4 py-3 bg-red-50 dark:bg-red-950/30 border-b border-red-200 dark:border-red-900/50">
          <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        </div>
      )}

      {/* Audio Jobs List */}
      <div className="flex-1 overflow-y-auto p-4">
        {audioJobs.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center px-6 text-center">
            <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-2xl mb-4">
              <Headphones className="h-10 w-10 text-purple-500 dark:text-purple-400" />
            </div>
            <h3 className="text-lg font-semibold text-slate-800 dark:text-white mb-2">
              No Audio Overviews Yet
            </h3>
            <p className="text-sm text-slate-500 dark:text-neutral-400 max-w-md">
              Generate an audio overview to get a podcast-style summary of your sources.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {audioJobs.map(job => (
              <div
                key={job.id}
                className={`p-4 bg-white dark:bg-neutral-800 border rounded-xl ${
                  currentAudio?.id === job.id
                    ? 'border-purple-300 dark:border-purple-700'
                    : 'border-slate-200 dark:border-neutral-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        job.status === 'COMPLETED'
                          ? 'bg-purple-100 dark:bg-purple-900/30'
                          : job.status === 'ERROR'
                            ? 'bg-red-100 dark:bg-red-900/30'
                            : 'bg-amber-100 dark:bg-amber-900/30'
                      }`}
                    >
                      {job.status === 'PROCESSING' || job.status === 'PENDING' ? (
                        <Loader2 className="h-5 w-5 text-amber-500 animate-spin" />
                      ) : job.status === 'ERROR' ? (
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                      ) : (
                        <Headphones className="h-5 w-5 text-purple-500" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800 dark:text-white">
                        {job.focus || 'Full Overview'}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-neutral-400">
                        {job.style} • {new Date(job.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {job.status === 'COMPLETED' && job.audioUrl && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handlePlayAudio(job)}
                        className={`p-2 rounded-lg transition-colors ${
                          currentAudio?.id === job.id && isPlaying
                            ? 'bg-purple-500 text-white'
                            : 'hover:bg-purple-100 dark:hover:bg-purple-900/30'
                        }`}
                      >
                        {currentAudio?.id === job.id && isPlaying ? (
                          <Pause className="h-5 w-5" />
                        ) : (
                          <Play className="h-5 w-5 text-purple-500" />
                        )}
                      </button>
                      <a
                        href={job.audioUrl}
                        download
                        className="p-2 hover:bg-slate-100 dark:hover:bg-neutral-700 rounded-lg transition-colors"
                        title="Download"
                      >
                        <Download className="h-5 w-5 text-slate-500" />
                      </a>
                    </div>
                  )}

                  {(job.status === 'PROCESSING' || job.status === 'PENDING') && (
                    <span className="px-2 py-1 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded-full">
                      Processing...
                    </span>
                  )}

                  {job.status === 'ERROR' && (
                    <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                      Failed
                    </span>
                  )}
                </div>

                {/* Draft notice */}
                <div className="mt-2 pt-2 border-t border-slate-100 dark:border-neutral-700">
                  <p className="text-[10px] text-slate-400 dark:text-neutral-500">
                    ⚡ Exploratory audio — not for official use
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AudioOverviewPanel;
